import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { AttendanceCode } from '@schooly/api';
import { EditIcon, SimpleButton, TagSelect, TagSelectProps } from '@schooly/style';
import React, { forwardRef, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { DropdownSelect } from '../DropdownSelect';
import { DropdownSelectFilterProps } from '../DropdownSelectFilter';

function AttendanceNotificationSelectComponent(
  {
    selected,
    onSelect,
    placeholder,
    options,
    onEmptyLayoutClick,
    ...dropdownProps
  }: DropdownSelectFilterProps<AttendanceCode> & {
    onEmptyLayoutClick: () => void;
    isDisabled?: (v: AttendanceCode) => boolean;
  },
  ref: React.ForwardedRef<DropdownSelect>,
) {
  const { $t } = useIntl();

  const renderTooltipTitle = useCallback(
    (c: AttendanceCode) => {
      if (!c.has_justification) return;

      return (
        <>
          <Typography mb={1.25}>
            {$t({ id: 'attendance-justification-NotificationsWillBeSent' })}
          </Typography>
          <Link to={`/settings/attendance/${c.id}`}>
            <SimpleButton startIcon={<EditIcon />} size="small">
              {$t({ id: 'attendance-EditAttendanceCode' })}
            </SimpleButton>
          </Link>
        </>
      );
    },
    [$t],
  );

  const renderContent = useCallback(() => {
    return (
      <Stack
        direction="row"
        flexWrap="wrap"
        sx={(theme) => ({
          margin: theme.spacing(1.25, 1.25, 3.5),
          gap: 1,
        })}
      >
        {!options.length ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            flex={1}
            sx={(theme) => ({
              margin: theme.spacing(2, 0),
              pb: theme.spacing(1),
            })}
          >
            <Stack gap={2} sx={{ maxWidth: 300, textAlign: 'center' }}>
              <Typography variant="h3">
                <FormattedMessage id="school-sections-Notifications-NoAttendanceStatuses" />
              </Typography>
              <Box>
                <Button color="primary" size="small" onClick={onEmptyLayoutClick}>
                  <FormattedMessage id="school-sections-Notifications-SetUpAttendanceCodes" />
                </Button>
              </Box>
            </Stack>
          </Stack>
        ) : (
          <>
            {renderAttendanceCodeTags({
              items: options,
              onClick: onSelect,
              tagProps: (item) => ({
                variant: selected.includes(item) ? 'filled' : undefined,
                disabled: item.has_justification,
              }),
              getTooltipTitle: renderTooltipTitle,
            })}
          </>
        )}
      </Stack>
    );
  }, [onEmptyLayoutClick, onSelect, options, renderTooltipTitle, selected]);

  return (
    <Stack
      width="506px"
      sx={(theme) => ({
        '& .emptyLabel': {
          ...theme.typography.body1,
        },
      })}
    >
      <DropdownSelect
        ref={ref}
        {...dropdownProps}
        placeholder={placeholder}
        hasValues={!!selected.length}
        renderContent={renderContent}
        layoutStyleProps={{
          minHeight: '35px',
          '.right-icon': {
            top: 8,
          },
        }}
      >
        {(opened) => (
          <>
            {renderAttendanceCodeTags({
              items: selected,
              tagProps: (c) => ({ size: 'small', disabled: c.has_justification }),
              onDelete: opened ? onSelect : undefined,
              getTooltipTitle: renderTooltipTitle,
            })}
          </>
        )}
      </DropdownSelect>
    </Stack>
  );
}

export const AttendanceNotificationSelect = forwardRef(AttendanceNotificationSelectComponent);

type AttendanceCodeRenderTagsParams = {
  items: AttendanceCode[];
  onDelete?: (v: AttendanceCode) => void;
  onClick?: (v: AttendanceCode) => void;
  tagProps?: TagSelectProps | ((v: AttendanceCode) => TagSelectProps);
  getTooltipTitle: (v: AttendanceCode) => React.ReactNode | undefined;
};

const renderAttendanceCodeTags = ({
  items,
  onDelete,
  onClick,
  tagProps,
  getTooltipTitle,
}: AttendanceCodeRenderTagsParams) => {
  return items.map((v) => {
    const props = typeof tagProps === 'function' ? tagProps(v) : tagProps;
    const isDisabled = !!props?.disabled;

    return (
      // biome-ignore lint/correctness/useJsxKeyInIterable: Legacy code
      <Tooltip
        title={getTooltipTitle(v)}
        componentsProps={{ tooltip: { sx: { width: 200, p: 1.25 } } }}
      >
        <div>
          <TagSelect
            key={v.id}
            onClick={onClick && !isDisabled ? () => onClick(v) : undefined}
            onDelete={onDelete && !isDisabled ? () => onDelete(v) : undefined}
            label={v.name}
            {...props}
          />
        </div>
      </Tooltip>
    );
  });
};
